import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import EventTypeService from '../../../services/EventTypeService';
import moment from 'moment';
import ModernCalendar from './ModernCalendar';
import TimeSlotPanel from './TimeSlotPanel';
import EventInfoHeader from './EventInfoHeader';
import { isMonthClosedByRollingCutoff } from '../../../utils/bookingWindow';
import {
  FullScreenContainer,
  StepContainer,
  SplitLayout,
  CalendarContainer,
} from './styled';

const DateTimeSelector = ({ eventType, onTimeSlotSelect, onBack }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [monthsCapStatus, setMonthsCapStatus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [monthCapMessage, setMonthCapMessage] = useState(null);

  useEffect(() => {
    if (selectedDate) {
      loadAvailability(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (eventType && eventType._id) {
      loadBlockedDates();
      if (eventType.availability?.monthlyCap?.monthsStatus) {
        setMonthsCapStatus(eventType.availability.monthlyCap.monthsStatus);
      }
    }
  }, [eventType]);

  const loadBlockedDates = async () => {
    try {
      const today = moment();
      const daysToFetch = eventType.advanceBookingDays === 0 ? 365 : (eventType.advanceBookingDays || 30);
      const endDate = today.clone().add(daysToFetch, 'days');
      
      const response = await EventTypeService.getPublicBlockedDates(
        eventType._id,
        today.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      );
      
      if (response.success) {
        setBlockedDates(response.data.blockedDates || []);
      }
    } catch (error) {
      console.error('Error loading blocked dates:', error);
    }
  };

  const loadAvailability = async (date) => {
    if (!date) {
      console.error('No date provided to loadAvailability');
      return;
    }
    
    try {
      setLoading(true);
      setMonthCapMessage(null);
      const response = await EventTypeService.getPublicAvailability(
        eventType._id, 
        date.format('YYYY-MM-DD')
      );
      
      if (response.success) {
        if (response.data.isBookingWindowClosed) {
          setMonthCapMessage({
            type: 'warning',
            message: response.data.reason || 'This month is not yet open for booking. Please select a different month.'
          });
          setAvailableSlots([]);
        } else if (response.data.isMonthlyCapReached) {
          setMonthCapMessage({
            type: 'warning',
            message: response.data.reason || `Booking limit for ${response.data.monthlyCapInfo?.monthName || 'this month'} has been reached. Please select a different month.`
          });
          setAvailableSlots([]);
        } else {
          setAvailableSlots(response.data.timeSlots || []);
        }
      } else {
        message.error('Failed to load available time slots');
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error loading availability:', error);
      message.error('Unable to load time slots. Please try again.');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    if (date.isBefore(moment(), 'day')) {
      message.warning('Please select a future date');
      return;
    }
    setSelectedDate(date);
    setSelectedTimeSlot(null);
  };

  const handleTimeSlotClick = (timeSlot) => {
    setSelectedTimeSlot(timeSlot);
  };

  const handleNextClick = () => {
    if (!selectedDate) {
      message.warning('Please select a date');
      return;
    }
    
    if (!selectedTimeSlot) {
      message.warning('Please select a time slot');
      return;
    }
    
    onTimeSlotSelect({
      ...selectedTimeSlot,
      date: selectedDate.format('YYYY-MM-DD')
    });
  };

  const isDateBlocked = (current) => {
    const currentDate = current.format('YYYY-MM-DD');
    return blockedDates.some(block => {
      return currentDate >= block.startDate && currentDate <= block.endDate;
    });
  };

  const isMonthCapReached = (current) => {
    if (!monthsCapStatus || monthsCapStatus.length === 0) return false;
    
    const monthKey = current.format('YYYY-MM');
    const monthStatus = monthsCapStatus.find(status => status.monthKey === monthKey);
    
    return monthStatus?.capReached === true;
  };

  const isMonthClosed = (current) => {
    const monthKey = current.format('YYYY-MM');
    const monthStatus = monthsCapStatus.find(status => status.monthKey === monthKey);
    if (monthStatus?.bookingClosed === true) return true;
    // Fallback in case monthsStatus has not loaded yet.
    return isMonthClosedByRollingCutoff(eventType, current);
  };

  const disabledDate = (current) => {
    const today = moment().startOf('day');
    
    if (current < today) {
      return true;
    }
    
    const advanceDays = eventType.advanceBookingDays;
    
    if (advanceDays !== undefined && advanceDays !== null && advanceDays > 0) {
      const maxDate = moment().add(advanceDays, 'days');
      if (current > maxDate) {
        return true;
      }
    }
    
    if (eventType.availability && eventType.availability.availableDays) {
      const dayOfWeek = current.day();
      if (!eventType.availability.availableDays.includes(dayOfWeek)) {
        return true;
      }
    }
    
    if (isDateBlocked(current)) {
      return true;
    }

    if (isMonthCapReached(current)) {
      return true;
    }

    if (isMonthClosed(current)) {
      return true;
    }
    
    return false;
  };

  return (
    <FullScreenContainer>
      <StepContainer>
        <EventInfoHeader eventType={eventType} onBack={onBack} />

        <SplitLayout>
          <CalendarContainer>
            <ModernCalendar
              value={selectedDate}
              onChange={handleDateSelect}
              disabledDate={disabledDate}
              isDateBlocked={isDateBlocked}
            />
          </CalendarContainer>

          <TimeSlotPanel
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            availableSlots={availableSlots}
            loading={loading}
            monthCapMessage={monthCapMessage}
            eventType={eventType}
            onTimeSlotClick={handleTimeSlotClick}
            onNextClick={handleNextClick}
          />
        </SplitLayout>
      </StepContainer>
    </FullScreenContainer>
  );
};

export default DateTimeSelector;
